import React from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import {
  compactTableActionButtonSx,
  tableApproveButtonSx,
  tableDeactivateButtonSx,
  tableRejectButtonSx,
  tableViewButtonSx,
} from '../../../styles/actionStyles';
import StatusChip from '../../../components/statusChip';
import CopyablePhone from '../../../components/copyablePhone';
import { personaLabel } from '../../../constant/personaLabel';


function ActionButton({ icon, label, color, backgroundColor, onClick, disabled = false }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      startIcon={<Icon icon={icon} width="18" height="18" />}
      sx={{
        ...compactTableActionButtonSx,
        backgroundColor,
        color,
        opacity: disabled ? 0.45 : 1,
        '&:hover': {
          backgroundColor,
        },
      }}
    >
      {label}
    </Button>
  );
}

export default function UserColumn(props) {
  const { navigate, requestApprovalAction, dareApprovals = {} } = props;

  return [
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1.1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F', fontWeight: 400 }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    // No Email column: it took the widest slot for a value reviewers rarely act on from the list.
    // The search box still matches email, and the user's details page shows it.
    {
      field: 'persona',
      headerName: 'User Persona',
      flex: 1,
      minWidth: 140,
      // Sort by what the reviewer reads, not by the stored code.
      valueGetter: (value) => (value ? personaLabel(value) : ''),
      renderCell: (params) => (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 0.9,
      minWidth: 150,
      renderCell: (params) => (
        <CopyablePhone
          phoneNumber={params.value}
          countryCode={params.row?.countryCode}
          textSx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}
        />
      ),
    },
    {
      // Resolved from the user's administrative location, not from the phone dial code — the two
      // disagree often enough that showing the dial code here would misreport where a user is.
      field: 'countryName',
      headerName: 'Country',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 120,
      flex: 0.9,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => <StatusChip status={params.value} />,
    },
    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 420,
      flex: 1.6,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const currentStatus = params.row.status;
        const approval = dareApprovals[params.row.id];
        // An active DARE whose sign-up still waits for eKutir can still be approved (that decides the sign-up).
        const dareWaitsForEkutir = approval?.status === 'PENDING' && approval?.approverType === 'EKUTIR';
        const isApproved = currentStatus === 'ACTIVE' && !dareWaitsForEkutir;
        const isRejected = currentStatus === 'REJECTED';
        const isInactive = currentStatus === 'INACTIVE' || currentStatus === 'DEACTIVATED';

        return (
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center" sx={{ width: '100%', minWidth: 0 }}>
            <ActionButton
              icon="material-symbols:visibility-outline-rounded"
              label="View"
              color={tableViewButtonSx.color}
              backgroundColor={tableViewButtonSx.bgcolor}
              onClick={() => navigate(`/userView/${params.row.id}?type=edit`)}
            />
            <ActionButton
              icon="material-symbols:check-rounded"
              label="Approve"
              color={tableApproveButtonSx.color}
              backgroundColor={tableApproveButtonSx.bgcolor}
              disabled={isApproved}
              onClick={() => requestApprovalAction('ACTIVE', [params.row.id])}
            />
            {isInactive ? (
              <ActionButton
                icon="material-symbols:delete-outline-rounded"
                label="Delete"
                color={tableRejectButtonSx.color}
                backgroundColor={tableRejectButtonSx.bgcolor}
                onClick={() => requestApprovalAction('DELETED', [params.row.id])}
              />
            ) : (
              <ActionButton
                icon="material-symbols:close-rounded"
                label="Reject"
                color={tableRejectButtonSx.color}
                backgroundColor={tableRejectButtonSx.bgcolor}
                disabled={isRejected}
                onClick={() => requestApprovalAction('REJECTED', [params.row.id])}
              />
            )}
            <ActionButton
              icon="material-symbols:block-rounded"
              label="Deactivate"
              color={tableDeactivateButtonSx.color}
              backgroundColor={tableDeactivateButtonSx.bgcolor}
              disabled={isInactive}
              onClick={() => requestApprovalAction('INACTIVE', [params.row.id])}
            />
          </Stack>
        );
      },
    },
  ];
}
